<!-- Pricing Modal -->
<app-pricing-modal
  [isOpen]="isPricingModalOpen()"
  [target]="paywallTarget()"
  (closed)="closePricingModal()"
  (productSelected)="onPricingProductSelected()"
  (paymentSuccess)="onPaymentSuccess($event)"
></app-pricing-modal>

<!-- Subscription Success Modal -->
<app-subscription-success-modal
  [isOpen]="isSubscriptionSuccessOpen()"
  [productName]="subscribedProductName()"
  (closed)="closeSubscriptionSuccess()"
></app-subscription-success-modal>

<div class="px-4 sm:px-8 pb-24 lg:pb-8 pt-20 lg:pt-24 overflow-y-auto h-full w-full print:hidden">
  <div class="max-w-2xl mx-auto space-y-6">
    <div class="bg-blue-50 border border-blue-200 rounded-xl p-4 sm:p-5 mb-6">
      <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h3 class="font-bold text-blue-900 text-base sm:text-lg mb-2">Administrative Plan Submission</h3>
          <p class="text-blue-700 text-sm">This is the lesson plan format required for administrative review and approval.</p>
        </div>
        <div class="shrink-0 flex items-center gap-2">
          <button
            (click)="handleExportDocx()"
            [disabled]="(isExportingDocx$ | async)"
            class="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm font-medium rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
          >
            @if (isExportingDocx$ | async) {
              <lucide-icon [img]="icons.Loader2" class="w-4 h-4 animate-spin"></lucide-icon>
              <span class="hidden sm:inline">Exporting...</span>
            } @else {
              <lucide-icon [img]="icons.FileText" class="w-4 h-4"></lucide-icon>
              <span>DOCX</span>
            }
          </button>
          <button
            (click)="handleExportGoogleDocs()"
            [disabled]="(isExportingGoogleDocs$ | async)"
            class="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-xs sm:text-sm font-medium rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
          >
            @if (isExportingGoogleDocs$ | async) {
              <lucide-icon [img]="icons.Loader2" class="w-4 h-4 animate-spin"></lucide-icon>
              <span class="hidden sm:inline">Creating...</span>
            } @else {
              <lucide-icon [img]="icons.FileSpreadsheet" class="w-4 h-4"></lucide-icon>
              <span>Docs</span>
            }
          </button>
        </div>
      </div>
    </div>

    @if (inputData$ | async; as inputData) {
      <!-- Lesson Overview -->
      <div class="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <h4 class="font-bold text-gray-900 text-lg mb-4 flex items-center gap-2">
          <span class="w-8 h-8 bg-brand/10 text-brand rounded-lg flex items-center justify-center text-sm font-bold">1</span>
          Lesson Overview
        </h4>
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
          <div>
            <span class="text-gray-500 block mb-1">Subject/Topic</span>
            <span class="font-medium text-gray-900">{{ inputData.topic }}</span>
          </div>
          <div>
            <span class="text-gray-500 block mb-1">Grade Level</span>
            <span class="font-medium text-gray-900">{{ inputData.grade }}</span>
          </div>
          <div>
            <span class="text-gray-500 block mb-1">Duration</span>
            <span class="font-medium text-gray-900">{{ inputData.duration }} minutes</span>
          </div>
        </div>
      </div>

      <!-- Learning Objectives -->
      <div class="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <h4 class="font-bold text-gray-900 text-lg mb-4 flex items-center gap-2">
          <span class="w-8 h-8 bg-brand/10 text-brand rounded-lg flex items-center justify-center text-sm font-bold">2</span>
          Learning Objectives
        </h4>
        <ul class="space-y-2 text-sm text-gray-700">
          <li class="flex items-start gap-2">
            <span class="w-1.5 h-1.5 bg-brand rounded-full mt-2 shrink-0"></span>
            <span>Students will understand core concepts related to {{ inputData.topic }}</span>
          </li>
          <li class="flex items-start gap-2">
            <span class="w-1.5 h-1.5 bg-brand rounded-full mt-2 shrink-0"></span>
            <span>Students will be able to apply knowledge through guided practice</span>
          </li>
          <li class="flex items-start gap-2">
            <span class="w-1.5 h-1.5 bg-brand rounded-full mt-2 shrink-0"></span>
            <span>Students will demonstrate mastery through assessment activities</span>
          </li>
        </ul>
      </div>
    }

    <!-- Standards Alignment -->
    @if (selectedObjectives$ | async; as objectives) {
      @if (objectives.length > 0) {
        <div class="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <h4 class="font-bold text-gray-900 text-lg mb-4 flex items-center gap-2">
            <span class="w-8 h-8 bg-brand/10 text-brand rounded-lg flex items-center justify-center text-sm font-bold">3</span>
            Standards Alignment
          </h4>
          <div class="space-y-4">
            @for (objective of objectives; track objective.id) {
              <div class="border-l-4 border-brand/30 pl-4">
                <div class="flex items-start gap-2 mb-1">
                  <span class="text-xs font-bold bg-brand/10 text-brand px-2 py-0.5 rounded shrink-0">{{ objective.standard }}</span>
                </div>
                @if (objective.standardText) {
                  <p class="text-sm text-gray-600 mb-2">{{ objective.standardText }}</p>
                }
                <p class="text-sm text-gray-900 font-medium">{{ objective.title }}</p>
              </div>
            }
          </div>
        </div>
      }
    }

    <!-- Lesson Structure -->
    <div class="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
      <h4 class="font-bold text-gray-900 text-lg mb-4 flex items-center gap-2">
        <span class="w-8 h-8 bg-brand/10 text-brand rounded-lg flex items-center justify-center text-sm font-bold">4</span>
        Lesson Structure
      </h4>
      @if (planStructure$ | async; as plan) {
        <div class="space-y-4">
          @for (block of plan; track block.id) {
            <div class="border border-gray-100 rounded-lg p-4 bg-gray-50/50">
              <div class="flex items-center justify-between mb-2">
                <h5 class="font-bold text-gray-900">{{ block.phase }}</h5>
                <span class="px-2.5 py-1 bg-gray-100 border border-gray-200 rounded-full text-xs font-mono font-bold text-gray-600">
                  {{ block.duration }} min
                </span>
              </div>
              <p class="text-sm font-medium text-gray-700 mb-2">{{ block.activity }}</p>
              @if (block.description) {
                <p class="text-sm text-gray-500 leading-relaxed">{{ block.description }}</p>
              }
            </div>
          }
        </div>
      }
    </div>

    <!-- Materials & Resources -->
    <div class="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
      <h4 class="font-bold text-gray-900 text-lg mb-4 flex items-center gap-2">
        <span class="w-8 h-8 bg-brand/10 text-brand rounded-lg flex items-center justify-center text-sm font-bold">5</span>
        Materials & Resources Required
      </h4>
      <ul class="space-y-2 text-sm text-gray-700">
        <li class="flex items-start gap-2">
          <span class="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 shrink-0"></span>
          <span>Presentation slides (generated)</span>
        </li>
        <li class="flex items-start gap-2">
          <span class="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 shrink-0"></span>
          <span>Whiteboard/Projector</span>
        </li>
        <li class="flex items-start gap-2">
          <span class="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 shrink-0"></span>
          <span>Student worksheets</span>
        </li>
        <li class="flex items-start gap-2">
          <span class="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 shrink-0"></span>
          <span>Assessment materials</span>
        </li>
      </ul>
    </div>

    <!-- Assessment Strategy -->
    <div class="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
      <h4 class="font-bold text-gray-900 text-lg mb-4 flex items-center gap-2">
        <span class="w-8 h-8 bg-brand/10 text-brand rounded-lg flex items-center justify-center text-sm font-bold">6</span>
        Assessment Strategy
      </h4>
      <div class="space-y-3 text-sm text-gray-700">
        <div class="flex items-start gap-3">
          <span class="text-xs font-bold bg-green-100 text-green-700 px-2 py-0.5 rounded">Formative</span>
          <span>Check for understanding questions during instruction, observation of student participation</span>
        </div>
        <div class="flex items-start gap-3">
          <span class="text-xs font-bold bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Summative</span>
          <span>End-of-lesson assessment activity, exit ticket review</span>
        </div>
      </div>
    </div>
  </div>
</div>
